import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  Paging,
  Pager,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";

import * as S from "./ExpedientesProduccion.styles";
import ExpedienteDetallePopup from "./ExpedienteDetallePopup";
import RechazoMotivoPopup from "./RechazoMotivoPopup";
import { apiService } from "../../../services/apiService";
import { ApiError } from "../../../services/apiError";
import { productosService } from "../../../services/productosService";
import { Sucursal } from "../../../dtos/Sucursal";
import {
  CatCabeceraManifold,
  CatCabeceraManifoldDecisionDto,
  ExpedienteEstadoUi,
  ExpedienteGridRow,
} from "../../../dtos/CatCabeceraManifold";
import { loadManifoldEnvases } from "../../../utils/manifoldEnvases";
import { generateExpedienteProduccionPdf } from "../../../utils/expedienteProduccionPdf";
import {
  getSessionSucursalId,
  isAdmin,
  isSupervisor,
} from "../../../helpers/session";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const p2 = (n: number) => String(n).padStart(2, "0");
const toYMD = (d: Date) =>
  `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const todayYMD = () => toYMD(new Date());
const subDaysYMD = (days: number) =>
  toYMD(new Date(Date.now() - days * 24 * 60 * 60 * 1000));

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    // Sin `response` la petición nunca llegó al backend (API caída, CORS o red).
    if (!error.response) {
      return {
        message:
          "No se pudo contactar con el servidor. Verifica que el API esté en ejecución.",
      };
    }
    return {
      message:
        error.response.data?.message ||
        `Ocurrió un error en el servidor (${error.response.status}).`,
      status: error.response.status,
      details: error.response.data?.details || null,
    };
  }
  if (err instanceof Error && err.message) {
    return { message: err.message };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const normalizeKey = (key: string) => key.replace(/[^a-z0-9]/gi, "").toLowerCase();

const pick = <T,>(
  obj: Record<string, unknown>,
  keys: string[],
): T | undefined => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return obj[key] as T;
    }
  }
  // El API alterna PascalCase, camelCase y guiones bajos entre endpoints
  // (Num_Ciclo, CapacidadTotal_M3), así que se compara sin separadores.
  const buscados = new Set(keys.map(normalizeKey));
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null && buscados.has(normalizeKey(key))) {
      return value as T;
    }
  }
  return undefined;
};

const isEmptyGuid = (value?: string | null) =>
  !value || value === EMPTY_GUID || value.replace(/-/g, "") === "";

const formatUserShortName = (nombre?: string | null) => {
  if (!nombre?.trim()) return "—";
  const parts = nombre.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first.charAt(0)}. ${last}`;
};

const mapEstadoUi = (estatusApi: string): ExpedienteEstadoUi => {
  const value = estatusApi.trim().toLowerCase();
  if (value === "aprobado") return "Aprobado";
  if (value === "rechazado") return "Rechazado";
  return "Pendiente de aprobar";
};

const estadoTone = (
  estado: ExpedienteEstadoUi,
): "warning" | "success" | "danger" => {
  if (estado === "Aprobado") return "success";
  if (estado === "Rechazado") return "danger";
  return "warning";
};

const parseLocalDate = (ymd: string): Date | null => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateOnly = (value: string | Date): Date => {
  const d = typeof value === "string" ? new Date(value) : value;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

interface UsuarioLite {
  id: string;
  nombre: string;
}

interface ManifoldLite {
  id_Manifold: number;
  id_Registro: number;
  nombre: string;
  id_Sucursal: number;
  turno: string;
}

interface SucursalOption extends Sucursal {
  idNum: number;
}

const normalizeCabecera = (raw: unknown): CatCabeceraManifold | null => {
  const item = (raw ?? {}) as Record<string, unknown>;
  const intCabecera = Number(
    pick(item, ["intCabecera", "IntCabecera"]) ?? 0,
  );
  if (!intCabecera) return null;
  return {
    intCabecera,
    id_Manifold: Number(
      pick(item, ["id_Manifold", "Id_Manifold", "idManifold"]) ?? 0,
    ),
    operador: String(pick(item, ["operador", "Operador"]) ?? ""),
    supervisor: String(pick(item, ["supervisor", "Supervisor"]) ?? ""),
    fechaLlenado: String(
      pick(item, ["fechaLlenado", "FechaLlenado"]) ?? "",
    ),
    producto: Number(pick(item, ["producto", "Producto"]) ?? 0),
    linea: Number(pick(item, ["linea", "Linea"]) ?? 0),
    parada: Number(
      pick(item, [
        "parada",
        "Parada",
        "numCiclo",
        "NumCiclo",
        "Num_Ciclo",
      ]) ?? 0,
    ),
    total_Envases: Number(
      pick(item, ["total_Envases", "Total_Envases", "totalEnvases"]) ?? 0,
    ),
    capacidadTotal_M3: Number(
      pick(item, [
        "capacidad",
        "Capacidad",
        "capacidadTotal_M3",
        "CapacidadTotal_M3",
        "capacidadTotalM3",
      ]) ?? 0,
    ),
    seccion_pruebas: pick(item, [
      "seccion_pruebas",
      "Seccion_pruebas",
      "seccionPruebas",
    ]) as number | null | undefined,
    estatus: String(pick(item, ["estatus", "Estatus"]) ?? "Revision"),
    motivo: (pick(item, ["motivo", "Motivo"]) as string | null) ?? null,
    fechaRegistro: String(
      pick(item, ["fechaRegistro", "FechaRegistro"]) ?? "",
    ),
    fechaEdicion:
      (pick(item, ["fechaEdicion", "FechaEdicion"]) as string | null) ?? null,
  };
};

const normalizeManifold = (raw: unknown): ManifoldLite | null => {
  const item = (raw ?? {}) as Record<string, unknown>;
  const idManifold = Number(
    pick(item, ["id_Manifold", "Id_Manifold", "idManifold"]) ?? 0,
  );
  if (!idManifold) return null;
  const registro = (pick<Record<string, unknown>>(item, [
    "registro",
    "Registro",
  ]) ?? {}) as Record<string, unknown>;
  const idRegistro = Number(
    pick(item, ["id_Registro", "Id_Registro", "idRegistro"]) ??
      pick(registro, ["id_Registro", "Id_Registro", "idRegistro"]) ??
      0,
  );
  const idSucursal = Number(
    pick(registro, ["id_Sucursal", "iD_Sucursal", "ID_Sucursal", "idSucursal"]) ??
      0,
  );
  const nombre = String(
    pick(registro, ["nombre", "Nombre"]) ?? `Manifold #${idManifold}`,
  );
  const turno = String(pick(item, ["turno", "Turno"]) ?? "");
  return {
    id_Manifold: idManifold,
    id_Registro: idRegistro,
    nombre,
    id_Sucursal: idSucursal,
    turno,
  };
};

/**
 * La parada del expediente es el Num_Ciclo de CatEstaciones, relacionado por
 * intCabecera. Se aísla en su propia petición para que la vista siga cargando
 * aunque el catálogo no esté disponible.
 */
const loadCiclosPorCabecera = async (): Promise<Map<number, number>> => {
  const ciclos = new Map<number, number>();
  try {
    const estaciones = await apiService.get<unknown[]>(
      "/api/Catalogos/CatEstaciones",
    );
    for (const raw of estaciones ?? []) {
      const item = (raw ?? {}) as Record<string, unknown>;
      const intCabecera = Number(pick(item, ["intCabecera", "IntCabecera"]) ?? 0);
      const numCiclo = Number(
        pick(item, ["numCiclo", "NumCiclo", "Num_Ciclo", "num_Ciclo"]) ?? 0,
      );
      if (intCabecera && numCiclo && !ciclos.has(intCabecera)) {
        ciclos.set(intCabecera, numCiclo);
      }
    }
  } catch {
    // Sin estaciones se usa la parada que traiga la cabecera.
  }
  return ciclos;
};

const normalizeUsuario = (raw: unknown): UsuarioLite | null => {
  const item = (raw ?? {}) as Record<string, unknown>;
  const id = String(pick(item, ["id", "Id"]) ?? "");
  if (!id) return null;
  return {
    id: id.toLowerCase(),
    nombre: String(pick(item, ["nombre", "Nombre"]) ?? ""),
  };
};

const getCurrentSupervisorId = (): string | null => {
  try {
    const stored = localStorage.getItem("userData");
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const id =
      parsed?.user?.id ??
      parsed?.user?.Id ??
      parsed?.id ??
      parsed?.Id ??
      null;
    return id ? String(id) : null;
  } catch {
    return null;
  }
};

const getCurrentSupervisorNombre = (): string => {
  try {
    const stored = localStorage.getItem("userData");
    if (!stored) return "";
    const parsed = JSON.parse(stored);
    return String(parsed?.user?.nombre ?? parsed?.nombre ?? "");
  } catch {
    return "";
  }
};

const ExpedientesProduccionIndex: React.FC = () => {
  const navigate = useNavigate();
  const restringirPorSucursal = isSupervisor() && !isAdmin();
  const sucursalSesion = getSessionSucursalId();

  const [allRows, setAllRows] = useState<ExpedienteGridRow[]>([]);
  const [rows, setRows] = useState<ExpedienteGridRow[]>([]);
  const [sucursales, setSucursales] = useState<SucursalOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(false);

  const [filterSucursal, setFilterSucursal] = useState(
    restringirPorSucursal && sucursalSesion ? String(sucursalSesion) : "",
  );
  const [filterManifold, setFilterManifold] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [filterFrom, setFilterFrom] = useState(subDaysYMD(22));
  const [filterTo, setFilterTo] = useState(todayYMD());

  const [detalleRow, setDetalleRow] = useState<ExpedienteGridRow | null>(null);
  const [detalleMode, setDetalleMode] = useState<"info" | "decision">("info");
  const [rejectVisible, setRejectVisible] = useState(false);

  const buildRows = useCallback(
    async (
      cabeceras: CatCabeceraManifold[],
      manifolds: ManifoldLite[],
      usuarios: UsuarioLite[],
      sucursalOptions: SucursalOption[],
      ciclosPorCabecera: Map<number, number>,
    ): Promise<ExpedienteGridRow[]> => {
      const manifoldsById = new Map(
        manifolds.map((m) => [m.id_Manifold, m]),
      );
      const usuariosById = new Map(usuarios.map((u) => [u.id, u]));
      const sucursalesById = new Map(
        sucursalOptions.map((s) => [s.idNum, s]),
      );

      const productCodes = [
        ...new Set(cabeceras.map((c) => c.producto).filter((c) => c > 0)),
      ];
      const productosByCodigo = new Map<number, string>();
      await Promise.all(
        productCodes.map(async (codigo) => {
          try {
            const prod = await productosService.getByCodigo(codigo);
            productosByCodigo.set(
              codigo,
              prod?.descripcion || `Producto ${codigo}`,
            );
          } catch {
            productosByCodigo.set(codigo, `Producto ${codigo}`);
          }
        }),
      );

      return cabeceras.map((c) => {
        const manifold = manifoldsById.get(c.id_Manifold);
        const idSucursal = manifold?.id_Sucursal ?? 0;
        const sucursal = sucursalesById.get(idSucursal);
        const operador = usuariosById.get(c.operador?.toLowerCase?.() ?? "");
        const supervisor = !isEmptyGuid(c.supervisor)
          ? usuariosById.get(c.supervisor.toLowerCase())
          : undefined;

        const numSuc = sucursal?.numSucursal ?? "";
        const nombreSuc = sucursal?.nombreSuc ?? "";
        const sucursalLabel =
          numSuc || nombreSuc
            ? `${numSuc}${numSuc && nombreSuc ? " - " : ""}${nombreSuc}`.trim()
            : idSucursal
              ? `Sucursal #${idSucursal}`
              : "—";

        return {
          intCabecera: c.intCabecera,
          id_Manifold: c.id_Manifold,
          id_Sucursal: idSucursal,
          numSucursal: sucursal?.numSucursal ?? "",
          sucursalLabel,
          supervisorId: c.supervisor,
          supervisorNombre: formatUserShortName(supervisor?.nombre),
          supervisorNombreCompleto: supervisor?.nombre?.trim() || "—",
          operadorId: c.operador,
          operadorNombre: formatUserShortName(operador?.nombre),
          productoCodigo: c.producto,
          productoNombre:
            productosByCodigo.get(c.producto) ||
            (c.producto ? `Producto ${c.producto}` : "—"),
          manifoldNombre: manifold?.nombre || String(c.id_Manifold || "—"),
          linea: c.linea,
          parada: ciclosPorCabecera.get(c.intCabecera) ?? c.parada,
          turno: manifold?.turno || "—",
          totalEnvases: c.total_Envases,
          capacidadTotal_M3: c.capacidadTotal_M3,
          estatusApi: c.estatus,
          estadoUi: mapEstadoUi(c.estatus),
          motivo: c.motivo,
          fechaLlenado: c.fechaLlenado,
          fechaRegistro: c.fechaRegistro,
        };
      });
    },
    [],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        cabecerasRes,
        manifoldsRes,
        usuariosRes,
        sucursalesRes,
        ciclosPorCabecera,
      ] = await Promise.all([
        apiService.get<unknown[]>("/api/Catalogos/CatCabeceraManifold"),
        apiService.get<unknown[]>("/api/Catalogos/CatManifold"),
        apiService.get<unknown[]>("/api/Usuarios"),
        apiService.get<Sucursal[]>("/api/Catalogos/Sucursales"),
        loadCiclosPorCabecera(),
      ]);

      const cabeceras = (cabecerasRes ?? [])
        .map(normalizeCabecera)
        .filter((c): c is CatCabeceraManifold => c != null);

      const manifolds = (manifoldsRes ?? [])
        .map(normalizeManifold)
        .filter((m): m is ManifoldLite => m != null);

      const usuarios = (usuariosRes ?? [])
        .map(normalizeUsuario)
        .filter((u): u is UsuarioLite => u != null);

      const sucursalOptions: SucursalOption[] = (sucursalesRes ?? []).map(
        (s) => ({
          ...s,
          idNum: Number(s.id),
        }),
      );

      const sucursalesVisibles = restringirPorSucursal
        ? sucursalOptions.filter(
            (s) => sucursalSesion != null && s.idNum === sucursalSesion,
          )
        : sucursalOptions;

      setSucursales(sucursalesVisibles);
      const gridRows = await buildRows(
        cabeceras,
        manifolds,
        usuarios,
        sucursalOptions,
        ciclosPorCabecera,
      );
      const scopedRows = restringirPorSucursal
        ? gridRows.filter(
            (row) =>
              sucursalSesion != null && row.id_Sucursal === sucursalSesion,
          )
        : gridRows;
      setAllRows(scopedRows);
      return scopedRows;
    } catch (err) {
      setAllRows([]);
      notify(handleApiError(err).message, "error", 3000);
      return [] as ExpedienteGridRow[];
    } finally {
      setLoading(false);
    }
  }, [buildRows, restringirPorSucursal, sucursalSesion]);

  const applyFilters = useCallback(
    (source: ExpedienteGridRow[]) => {
      const from = parseLocalDate(filterFrom);
      const to = parseLocalDate(filterTo);
      const manifoldTerm = filterManifold.trim().toLowerCase();
      const sucursalId = restringirPorSucursal
        ? sucursalSesion
        : filterSucursal
          ? Number(filterSucursal)
          : null;

      return source.filter((row) => {
        if (restringirPorSucursal) {
          if (!sucursalSesion || row.id_Sucursal !== sucursalSesion) return false;
        } else if (sucursalId && row.id_Sucursal !== sucursalId) {
          return false;
        }

        if (
          manifoldTerm &&
          !row.manifoldNombre.toLowerCase().includes(manifoldTerm)
        ) {
          return false;
        }

        if (filterEstado && row.estadoUi !== filterEstado) return false;

        const baseDate = row.fechaLlenado || row.fechaRegistro;
        if (baseDate && (from || to)) {
          const day = dateOnly(baseDate);
          if (from && day < from) return false;
          if (to && day > to) return false;
        }

        return true;
      });
    },
    [filterFrom, filterTo, filterManifold, filterSucursal, filterEstado, restringirPorSucursal, sucursalSesion],
  );

  const refreshAndKeepFilters = useCallback(async () => {
    const source = await loadData();
    const filtered = applyFilters(source);
    setRows(filtered);
    return filtered;
  }, [applyFilters, loadData]);

  const handleBuscar = useCallback(async () => {
    const source = allRows.length > 0 ? allRows : await loadData();
    const filtered = applyFilters(source);
    setRows(filtered);
    if (!filtered.length) {
      notify({ message: "Sin resultados.", type: "info", displayTime: 1800 });
    }
  }, [allRows, applyFilters, loadData]);

  useEffect(() => {
    (async () => {
      const source = await loadData();
      setRows(applyFilters(source));
    })();
    // Carga inicial únicamente
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingCount = useMemo(
    () => rows.filter((r) => r.estadoUi === "Pendiente de aprobar").length,
    [rows],
  );

  const generatePdfForRow = async (
    row: ExpedienteGridRow,
    supervisorOverride?: string,
  ) => {
    const envasesData = await loadManifoldEnvases(row.id_Manifold, {
      intCabecera: row.intCabecera,
      parada: row.parada,
      fechaLlenado: row.fechaLlenado,
    });

    if (envasesData.sinNumeroSerie > 0) {
      throw new Error(
        `No se obtuvo el número de serie de ${envasesData.sinNumeroSerie} envase(s). El expediente no se emite incompleto.`,
      );
    }

    const esperados = Number(row.totalEnvases) || 0;
    if (esperados > 0 && envasesData.series.length !== esperados) {
      throw new Error(
        `El expediente registra ${esperados} envase(s) y se obtuvieron ${envasesData.series.length}. El expediente no se emite incompleto.`,
      );
    }

    const supervisorNombreCompleto =
      supervisorOverride ||
      (row.supervisorNombreCompleto !== "—"
        ? row.supervisorNombreCompleto
        : getCurrentSupervisorNombre());

    await generateExpedienteProduccionPdf({
      row: {
        ...row,
        supervisorNombre:
          row.supervisorNombre !== "—"
            ? row.supervisorNombre
            : formatUserShortName(supervisorNombreCompleto),
        supervisorNombreCompleto:
          supervisorNombreCompleto || row.supervisorNombreCompleto,
      },
      series: envasesData.series,
      horaInicio: envasesData.horaInicio,
      horaTermino: envasesData.horaTermino,
      supervisorNombreCompleto,
    });
  };

  const sendDecision = async (aprobado: boolean, motivo?: string) => {
    if (!detalleRow) return;
    const supervisorId = getCurrentSupervisorId();
    if (!supervisorId) {
      notify(
        "No se pudo identificar al supervisor en sesión.",
        "error",
        3000,
      );
      return;
    }

    setDeciding(true);
    try {
      const body: CatCabeceraManifoldDecisionDto = {
        supervisor: supervisorId,
        aprobado,
        motivo: aprobado ? null : motivo,
      };
      await apiService.patch(
        `/api/Catalogos/CatCabeceraManifold/${detalleRow.intCabecera}/decision`,
        body,
      );

      if (aprobado) {
        const supervisorNombre = getCurrentSupervisorNombre();
        notify("Expediente aprobado. Generando PDF...", "success", 2200);
        try {
          await generatePdfForRow(detalleRow, supervisorNombre);
        } catch {
          notify(
            "Expediente aprobado, pero no se pudo generar el PDF. Usa Descarga después.",
            "warning",
            3500,
          );
        }
      } else {
        notify("Expediente rechazado.", "success", 2200);
      }

      setDetalleRow(null);
      setRejectVisible(false);
      await refreshAndKeepFilters();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setDeciding(false);
    }
  };

  const handleDescargaPdf = async (row: ExpedienteGridRow) => {
    try {
      notify("Generando PDF...", "info", 1500);
      await generatePdfForRow(row);
      notify("PDF descargado.", "success", 1800);
    } catch (err) {
      notify(
        `No se pudo generar el PDF. ${handleApiError(err).message}`,
        "error",
        4000,
      );
    }
  };

  const openDetalle = (
    row: ExpedienteGridRow,
    mode: "info" | "decision",
  ) => {
    setDetalleMode(mode);
    setDetalleRow(row);
  };

  const renderEstado = (row: ExpedienteGridRow) => (
    <S.EstadoCell $tone={estadoTone(row.estadoUi)}>
      <span>{row.estadoUi}</span>
      <S.InfoButton
        type="button"
        title="Ver detalle"
        onClick={() => openDetalle(row, "info")}
      >
        i
      </S.InfoButton>
    </S.EstadoCell>
  );

  const renderAccion = (row: ExpedienteGridRow) => {
    if (row.estadoUi !== "Pendiente de aprobar") {
      return <S.MutedText>—</S.MutedText>;
    }
    return (
      <S.ActionButton type="button" onClick={() => openDetalle(row, "decision")}>
        Aprobar
      </S.ActionButton>
    );
  };

  const renderPdf = (row: ExpedienteGridRow) => {
    if (row.estadoUi !== "Aprobado") {
      return <S.MutedText>No disponible</S.MutedText>;
    }
    return (
      <S.ActionButton type="button" onClick={() => handleDescargaPdf(row)}>
        Descarga
      </S.ActionButton>
    );
  };

  return (
    <>
      <S.DevExtremeModalGlobalStyles />
      <S.PageContainer>
        <div style={{ marginBottom: 12 }}>
          <Button
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            onClick={() => navigate("/reportes")}
          />
        </div>

        <S.HeaderContainer>
          <S.Title>Reportes - expedientes de producción</S.Title>
        </S.HeaderContainer>

        <S.FilterBar>
          <S.Field>
            <S.Label>Sucursal</S.Label>
            <S.StyledSelect
              value={
                restringirPorSucursal && sucursalSesion
                  ? String(sucursalSesion)
                  : filterSucursal
              }
              disabled={restringirPorSucursal}
              onChange={(e) => setFilterSucursal(e.target.value)}
            >
              {!restringirPorSucursal && <option value="">Todas</option>}
              {sucursales.map((s) => (
                <option key={s.idNum} value={s.idNum}>
                  {s.numSucursal} - {s.nombreSuc}
                </option>
              ))}
            </S.StyledSelect>
          </S.Field>

          <S.Field>
            <S.Label>Manifold</S.Label>
            <S.StyledInput
              value={filterManifold}
              placeholder="Buscar manifold"
              onChange={(e) => setFilterManifold(e.target.value)}
            />
          </S.Field>

          <S.Field>
            <S.Label>Estado</S.Label>
            <S.StyledSelect
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="Pendiente de aprobar">Pendiente de aprobar</option>
              <option value="Aprobado">Aprobado</option>
              <option value="Rechazado">Rechazado</option>
            </S.StyledSelect>
          </S.Field>

          <S.Field>
            <S.Label>Inicio</S.Label>
            <S.DateInput
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
            />
          </S.Field>

          <S.Field>
            <S.Label>Fin</S.Label>
            <S.DateInput
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
            />
          </S.Field>

          <Button
            text="Buscar"
            type="default"
            stylingMode="contained"
            onClick={handleBuscar}
            disabled={loading}
          />
        </S.FilterBar>

        <S.GridContainer>
          <DataGrid
            dataSource={rows}
            keyExpr="intCabecera"
            showBorders={false}
            columnAutoWidth
            rowAlternationEnabled
            hoverStateEnabled
            wordWrapEnabled
            noDataText={loading ? "Cargando..." : "Sin expedientes"}
          >
            <FilterRow visible={false} />
            <HeaderFilter visible />
            <Paging defaultPageSize={10} />
            <Pager
              visible
              showPageSizeSelector
              allowedPageSizes={[10, 20, 50]}
              showInfo
            />

            <Column
              dataField="sucursalLabel"
              caption="Sucursal"
              minWidth={140}
            />
            <Column
              dataField="supervisorNombre"
              caption="Supervisor"
              minWidth={120}
            />
            <Column
              dataField="operadorNombre"
              caption="Operador"
              minWidth={120}
            />
            <Column
              dataField="productoNombre"
              caption="Producto"
              minWidth={160}
            />
            <Column
              dataField="manifoldNombre"
              caption="Manifold"
              width={110}
              alignment="center"
            />
            <Column
              dataField="totalEnvases"
              caption="Envases"
              width={100}
              alignment="center"
            />
            <Column
              caption="Estado"
              minWidth={180}
              cellRender={({ data }) => renderEstado(data as ExpedienteGridRow)}
            />
            <Column
              caption="Acción"
              width={120}
              alignment="center"
              allowSorting={false}
              cellRender={({ data }) => renderAccion(data as ExpedienteGridRow)}
            />
            <Column
              caption="PDF"
              width={140}
              alignment="center"
              allowSorting={false}
              cellRender={({ data }) => renderPdf(data as ExpedienteGridRow)}
            />
          </DataGrid>
        </S.GridContainer>

        {!loading && pendingCount > 0 && (
          <S.MutedText style={{ display: "block", marginTop: 10 }}>
            {pendingCount} expediente(s) pendiente(s) de aprobar en la vista
            actual.
          </S.MutedText>
        )}
      </S.PageContainer>

      <ExpedienteDetallePopup
        visible={!!detalleRow}
        row={detalleRow}
        mode={detalleMode}
        deciding={deciding}
        onClose={() => {
          if (!rejectVisible && !deciding) setDetalleRow(null);
        }}
        onAprobar={() => sendDecision(true)}
        onRequestReject={() => setRejectVisible(true)}
      />

      <RechazoMotivoPopup
        visible={rejectVisible && !!detalleRow}
        deciding={deciding}
        onClose={() => {
          if (!deciding) setRejectVisible(false);
        }}
        onConfirm={(motivo) => sendDecision(false, motivo)}
      />
    </>
  );
};

export default ExpedientesProduccionIndex;
